import { useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrAspregel } from './typen';
import { ASP_ART } from './gemeinsam';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, Feld, FormSection,
  StatusBadge, type Spalte,
} from '../../muster';

/**
 * ASP-Regeln — in Reihenfolge (die erste treffende Regel entscheidet).
 *
 * Umzug U6c: Liste und Bearbeitung getrennt (`DataTable` + `Drawer`); die
 * aufklappende Zeile mit neun Feldern nebeneinander war bei 1440 Breite
 * zweizeilig und ohne erkennbare Zuordnung von Beschriftung zu Feld.
 */
export function AspRegeln({ api, projektId, regeln, onGeaendert }: {
  api: ApiClient; projektId: string; regeln: MsrAspregel[]; onGeaendert: () => void;
}) {
  const [entwurf, setEntwurf] = useState<MsrAspregel | null>(null);
  const [speichertLaeuft, setSpeichertLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const bearbeiten = (r: MsrAspregel) => { setEntwurf({ ...r }); setFehler(null); };

  const aendern = (teil: Partial<MsrAspregel>) =>
    setEntwurf((v) => (v ? { ...v, ...teil } : v));

  const speichern = async () => {
    if (!entwurf) return;
    setSpeichertLaeuft(true);
    setFehler(null);
    try {
      const { id, ...rest } = entwurf;
      await api.msrAspregelSetzen(projektId, id, rest);
      setEntwurf(null);
      onGeaendert();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichertLaeuft(false);
    }
  };

  const spalten: Spalte<MsrAspregel>[] = [
    { schluessel: 'reihenfolge', titel: 'Reihenfolge', art: 'zahl', breite: '110px',
      titelHinweis: 'Die erste treffende Regel entscheidet.', wert: (r) => r.reihenfolge },
    { schluessel: 'gewerk', titel: 'Gewerk', wert: (r) => r.gewerk },
    { schluessel: 'bauteilart', titel: 'Bauteilart', wert: (r) => r.bauteilart ?? 'alle' },
    { schluessel: 'zuordnungsart', titel: 'Zuordnungsart',
      wert: (r) => ASP_ART[r.zuordnungsart] },
    {
      schluessel: 'aspMuster', titel: 'ASP-Muster', immerSichtbar: true,
      wert: (r) => r.aspMuster,
      inhalt: (r) => <span style={{ fontFamily: 'monospace' }}>{r.aspMuster}</span>,
    },
    { schluessel: 'maxLeitungslaengeM', titel: 'Max. Leitungslänge', einheit: 'm',
      art: 'zahl', wert: (r) => r.maxLeitungslaengeM ?? null },
    { schluessel: 'begruendung', titel: 'Begründung', wert: (r) => r.begruendung ?? null },
    {
      schluessel: 'istAktiv', titel: 'Zustand', art: 'status', breite: '120px',
      wert: (r) => (r.istAktiv ? 'aktiv' : 'stillgelegt'),
      inhalt: (r) => (r.istAktiv
        ? <StatusBadge klein zustand="gut" text="aktiv" />
        : <StatusBadge klein zustand="neutral" text="stillgelegt" />),
    },
    {
      schluessel: 'aktion', titel: 'Ändern', breite: '120px', sortierbar: false,
      wert: () => '',
      inhalt: (r) => (
        <button type="button" className="m-knopf" onClick={() => bearbeiten(r)}>
          Bearbeiten
        </button>
      ),
    },
  ];

  return (
    <ContentSection
      titel="ASP-Regeln"
      beschreibung="In Reihenfolge abgearbeitet — die erste treffende Regel entscheidet."
    >
      <DataTable
        name="ASP-Regeln"
        spalten={spalten}
        zeilen={regeln}
        schluessel={(r) => r.id}
        sortierung={{ spalte: 'reihenfolge', richtung: 'auf' }}
        dicht
        ausfuhr
        ausfuhrDateiname="msr-aspregeln"
        aktiveZeile={entwurf?.id}
        zeilenKlasse={(r) => (r.istAktiv ? undefined : 'schwach')}
        kontextmenue={[{ beschriftung: 'Bearbeiten …', beiWahl: bearbeiten }]}
        leer={(
          <EmptyState
            titel="Für dieses Projekt ist keine ASP-Regel hinterlegt."
            erklaerung={'Ohne Regel ordnet die Ableitung kein Feldgerät einem '
              + 'Schaltschrank zu — die Regeln kommen aus der Projektvorlage.'}
          />
        )}
      />

      <Drawer
        offen={entwurf !== null}
        titel={entwurf ? `ASP-Regel ${entwurf.reihenfolge}` : ''}
        beschreibung={entwurf ? `Gewerk ${entwurf.gewerk}` : undefined}
        beiSchliessen={() => setEntwurf(null)}
        fuss={(
          <>
            <button type="button" className="m-knopf m-knopf-haupt" disabled={speichertLaeuft}
              onClick={() => void speichern()}>
              {speichertLaeuft ? 'speichert …' : 'Speichern'}
            </button>
            <button type="button" className="m-knopf" onClick={() => setEntwurf(null)}>
              Abbrechen
            </button>
          </>
        )}
      >
        {entwurf && (
          <FormSection titel="Regel" spalten={2}>
            <Feld art="zahl" beschriftung="Reihenfolge" wert={entwurf.reihenfolge}
              hinweis="Kleinere Zahl wird zuerst geprüft."
              beiAenderung={(w) => aendern({ reihenfolge: (w as number | null) ?? 0 })} />
            <Feld beschriftung="Gewerk" wert={entwurf.gewerk}
              beiAenderung={(w) => aendern({ gewerk: w as string })} />
            <Feld beschriftung="Bauteilart" wert={entwurf.bauteilart ?? ''}
              platzhalter="leer = alle"
              beiAenderung={(w) => aendern({ bauteilart: w as string })} />
            <Feld art="auswahl" beschriftung="Zuordnungsart" wert={entwurf.zuordnungsart}
              optionen={Object.entries(ASP_ART)
                .map(([k, n]) => ({ wert: k, beschriftung: n }))}
              beiAenderung={(w) => aendern({
                zuordnungsart: w as MsrAspregel['zuordnungsart'],
              })} />
            <Feld beschriftung="ASP-Muster" wert={entwurf.aspMuster} breite="voll"
              beiAenderung={(w) => aendern({ aspMuster: w as string })} />
            <Feld art="zahl" beschriftung="Max. Leitungslänge" einheit="m"
              wert={entwurf.maxLeitungslaengeM ?? null}
              beiAenderung={(w) => aendern({
                maxLeitungslaengeM: (w as number | null) ?? undefined,
              })} />
            <Feld art="schalter" beschriftung="Regel aktiv" wert={entwurf.istAktiv}
              beiAenderung={(w) => aendern({ istAktiv: w as boolean })} />
            <Feld beschriftung="Begründung" wert={entwurf.begruendung ?? ''} breite="voll"
              beiAenderung={(w) => aendern({ begruendung: w as string })} />
          </FormSection>
        )}

        {fehler && (
          <ErrorState
            klein
            meldung="Die ASP-Regel konnte nicht gespeichert werden."
            technisch={fehler}
            beiErneutVersuchen={() => void speichern()}
          />
        )}
      </Drawer>
    </ContentSection>
  );
}
